import { createMemo, createSignal, For, Show, type JSX } from 'solid-js';
import { eventTime, eventTone, splitEventMessage } from '../lib/format';
import { flatten, useBoxEvents } from '../lib/queries';
import type { BoxEventItem } from '../lib/types';
import { Badge } from '../ui/Badge';
import { Button, IconButton } from '../ui/Button';
import { Card } from '../ui/Card';
import { Icon } from '../ui/Icon';
import { ErrorAlert, LoadingRows } from './states';

function EventRow(props: { event: BoxEventItem }): JSX.Element {
  const [open, setOpen] = createSignal(false);
  const parts = createMemo(() => splitEventMessage(props.event.msg));

  return (
    <div class="list-row event-row">
      <Icon name={props.event.level === 'Error' ? 'alert' : 'activity'} size={18} />
      <div class="list-row__main">
        <span class="list-row__title">{parts().summary}</span>
        <span class="list-row__meta">{eventTime(props.event.time)}</span>
        <Show when={open() && parts().detail}>
          <pre class="event-row__detail">{parts().detail}</pre>
        </Show>
      </div>
      <Badge tone={eventTone(props.event.level)}>{props.event.level}</Badge>
      <Show when={parts().detail}>
        <IconButton label={open() ? 'Hide details' : 'Show details'} small onClick={() => setOpen(!open())}>
          <Icon name={open() ? 'chevronDown' : 'chevronRight'} size={16} />
        </IconButton>
      </Show>
    </div>
  );
}

/** Background jobs report themselves here and nowhere else: a stack deployment
 *  that fails still leaves the box Ready (rust_code/box_jobs/src/deploy_stack.rs:32),
 *  so this feed is the only place the failure is visible. */
export function BoxEvents(props: { boxId: string; busy: boolean }): JSX.Element {
  const events = useBoxEvents(
    () => props.boxId,
    () => props.busy,
  );
  const items = createMemo(() => flatten(events.data?.pages));

  return (
    <Card padding="lg">
      <div class="section__title">
        <h3>Activity</h3>
        <Button variant="ghost" size="sm" loading={events.isFetching} onClick={() => void events.refetch()}>
          <Icon name="refresh" />
          Refresh
        </Button>
      </div>

      <Show when={events.isError}>
        <ErrorAlert title="Could not load activity" error={events.error} onRetry={() => void events.refetch()} />
      </Show>

      <Show when={!events.isPending} fallback={<LoadingRows rows={2} height={64} />}>
        <Show
          when={items().length > 0}
          fallback={
            <span class="muted text-sm">
              Nothing to report. Failed deployments and other problems show up here.
            </span>
          }
        >
          <div class="list">
            <For each={items()}>{(event) => <EventRow event={event} />}</For>
          </div>
          <Show when={events.hasNextPage}>
            <div class="row gap-3" style={{ 'justify-content': 'center', 'margin-top': 'var(--space-4)' }}>
              <Button
                variant="secondary"
                size="sm"
                loading={events.isFetchingNextPage}
                onClick={() => void events.fetchNextPage()}
              >
                Load older
              </Button>
            </div>
          </Show>
        </Show>
      </Show>
    </Card>
  );
}
